import { Box, Button, Flex, Text } from '@nrx/ui';
import type { JSX } from 'react';

type GroupConsultViewMode = 'list' | 'month';

type GroupConsultViewToggleProps = {
  viewMode: GroupConsultViewMode;
  onViewModeChange: (viewMode: GroupConsultViewMode) => void;
};

const VIEW_MODES: readonly { value: GroupConsultViewMode; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'month', label: 'Month' },
];

/**
 * Toggle between the existing session list and the alternate month calendar.
 *
 * @remarks `Button` with `aria-pressed`, not `Tabs` — same reason as the scheduling-mode strip in
 * `consult-scheduling-page.tsx`: the page and month-view specs reach these two controls as
 * `getByRole('button', { name: 'Month' })`, and `Tabs.Tab` would make them `role="tab"`.
 * `aria-pressed` was already here and is preserved, so the pressed state has a non-colour signal.
 */
export function GroupConsultViewToggle({
  viewMode,
  onViewModeChange,
}: GroupConsultViewToggleProps): JSX.Element {
  return (
    <Flex align="center" justify="between" wrap gap="3" className="consult-view-toggle">
      <Box>
        <Text as="p" size="2" weight="medium">
          View
        </Text>
        <Text as="p" size="2" color="muted">
          Switch between the session list and month calendar.
        </Text>
      </Box>
      <Flex role="group" aria-label="Group consult view" gap="2">
        {VIEW_MODES.map(({ value, label }) => (
          <Button
            key={value}
            tone={viewMode === value ? 'primary' : 'secondary'}
            aria-pressed={viewMode === value}
            onClick={() => onViewModeChange(value)}
            className="consult-view-toggle__button"
          >
            {label}
          </Button>
        ))}
      </Flex>
    </Flex>
  );
}
